/**
 * API Service for DGT7 Simulator
 */
import axios from 'axios';

// Use empty string for relative URLs (proxied through nginx/reverse proxy)
// This allows the app to work with HTTPS when behind a reverse proxy
// IMPORTANT: Default to empty string for production builds
const envApiUrl = process.env.REACT_APP_API_URL;
const API_BASE_URL = (envApiUrl && envApiUrl !== '' && envApiUrl !== 'undefined' && envApiUrl !== 'RELATIVE_URL') ? envApiUrl : '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Device Management APIs
export const deviceAPI = {
  // Get all devices
  getAll: async () => {
    const response = await api.get('/api/devices');
    return response.data;
  },

  // Create new device
  create: async (deviceData) => {
    const response = await api.post('/api/devices', deviceData);
    return response.data;
  },

  // Get device by ID
  getById: async (deviceId) => {
    const response = await api.get(`/api/devices/${deviceId}`);
    return response.data;
  },

  // Update device
  update: async (deviceId, updateData) => {
    const response = await api.put(`/api/devices/${deviceId}`, updateData);
    return response.data;
  },

  // Delete device
  delete: async (deviceId) => {
    await api.delete(`/api/devices/${deviceId}`);
  },
};

// Simulation Control APIs
export const simulationAPI = {
  // Start simulation for a device
  start: async (deviceId) => {
    const response = await api.post(`/api/simulation/start/${deviceId}`);
    return response.data;
  },

  // Stop simulation for a device
  stop: async (deviceId) => {
    const response = await api.post(`/api/simulation/stop/${deviceId}`);
    return response.data;
  },

  // Batch start simulations for multiple devices
  batchStart: async (deviceIds) => {
    const response = await api.post('/api/simulation/batch-start', {
      device_ids: deviceIds,
    });
    return response.data;
  },

  // Batch stop simulations for multiple devices
  batchStop: async (deviceIds) => {
    const response = await api.post('/api/simulation/batch-stop', {
      device_ids: deviceIds,
    });
    return response.data;
  },

  // Stop all simulations
  stopAll: async () => {
    const response = await api.post('/api/simulation/stop-all');
    return response.data;
  },

  // Get all simulation statuses
  getAllStatuses: async () => {
    const response = await api.get('/api/simulation/status');
    return response.data;
  },

  // Get status for specific device
  getStatus: async (deviceId) => {
    const response = await api.get(`/api/simulation/status/${deviceId}`);
    return response.data;
  },
};

// Configuration APIs
export const configAPI = {
  // Get current endpoint configuration
  getEndpoint: async () => {
    const response = await api.get('/api/config/endpoint');
    return response.data;
  },

  // Update endpoint configuration
  updateEndpoint: async (endpointConfig) => {
    const response = await api.put('/api/config/endpoint', endpointConfig);
    return response.data;
  },

  // Get simulation settings
  getSettings: async () => {
    const response = await api.get('/api/config/settings');
    return response.data;
  },

  // Health check
  healthCheck: async () => {
    const response = await api.get('/api/health');
    return response.data;
  },
};

// WebSocket connection for real-time monitoring
export class MonitorWebSocket {
  constructor(onMessage, onError, onClose) {
    this.ws = null;
    this.onMessage = onMessage;
    this.onError = onError;
    this.onClose = onClose;
    this.reconnectInterval = 5000;
    this.reconnectTimer = null;
  }

  connect() {
    // Construct WebSocket URL
    let wsUrl;
    if (API_BASE_URL === '' || API_BASE_URL === '/') {
      // Use relative WebSocket URL (will use same protocol/host as page)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws/monitor`;
    } else {
      wsUrl = API_BASE_URL.replace('http', 'ws') + '/ws/monitor';
    }
    console.log('Connecting to WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (this.onMessage) {
            this.onMessage(data);
          }
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (this.onError) {
          this.onError(error);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket closed');
        if (this.onClose) {
          this.onClose();
        }
        // Attempt to reconnect
        this.reconnect();
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.reconnect();
    }
  }

  reconnect() {
    if (this.reconnectTimer) {
      return;
    }
    this.reconnectTimer = setTimeout(() => {
      console.log('Attempting to reconnect WebSocket...');
      this.connect();
    }, this.reconnectInterval);
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(typeof data === 'string' ? data : JSON.stringify(data));
    }
  }

  requestStatus() {
    this.send('get_status');
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

export default api;


